import { useState } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Center,
  Flex,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';

import {
  ArrowLeftOutline,
  Badge,
  BadgeStatus,
  DocumentDownloadOutline,
} from '@aslanpay/portal-common-lib';

import RefreshButton from 'app/components/Reconciliations/RefreshButton';
import SalaryDiscrepanciesTab from 'app/components/Reconciliations/SalaryDiscrepanciesTab';
import SalaryNotCheckedTab from 'app/components/Reconciliations/SalaryNotCheckedTab';
import SalaryReconciliationCompaniesTab from 'app/components/Reconciliations/SalaryReconciliationCompaniesTab';
import SalaryReconciliationProblemsTab from 'app/components/Reconciliations/SalaryReconciliationProblemsTab';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import EmptyState from 'app/components/UI/EmptyState';
import ProfileField from 'app/components/UI/ProfileField';

import {
  RECONCILIATION_COMPANY_STATUS_PARAM,
  RECONCILIATION_TAB_PARAM,
  ReconciliationRunStatusValues,
} from 'app/constants/reconciliations';
import {
  SalaryDiscrepancyTabKey,
  SalaryReconciliationTabKey,
  SalaryReconciliationTabs,
} from 'app/constants/salaryReconciliations';

import useDownloadSalaryReconciliationReport from 'app/hooks/useDownloadSalaryReconciliationReport';
import { SALARY_DISCREPANCIES_QUERY_KEY } from 'app/hooks/useFetchSalaryDiscrepancies';
import useFetchSalaryReconciliation, {
  SALARY_RECONCILIATION_QUERY_KEY,
} from 'app/hooks/useFetchSalaryReconciliation';
import { SALARY_RECONCILIATION_COMPANIES_QUERY_KEY } from 'app/hooks/useFetchSalaryReconciliationCompanies';
import useFetchSalaryReconciliationReport, {
  SALARY_RECONCILIATION_REPORT_QUERY_KEY,
} from 'app/hooks/useFetchSalaryReconciliationReport';
import { SALARY_UNCHECKED_INDIVIDUALS_QUERY_KEY } from 'app/hooks/useFetchSalaryUncheckedIndividuals';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import { SalaryReconciliationCompanyStatus } from 'app/types/salaryReconciliations';

import {
  formatDateTime,
  formatRunDuration,
  isActiveReconciliationRun,
} from 'app/utils/reconciliations';
import { getReconciliationsPath } from 'app/utils/routes';

const COMPANY_STATUSES: SalaryReconciliationCompanyStatus[] = ['CHECKED', 'FAILED'];
const DISCREPANCY_TABS: SalaryDiscrepancyTabKey[] = ['new', 'resolved', 'past', 'fixed'];

/**
 * @description Builds the link that opens a tab of the run, optionally with the companies tab narrowed to a status.
 */
const tabLink = (
  tab: SalaryReconciliationTabKey,
  companyStatus?: SalaryReconciliationCompanyStatus
) => {
  const params = new URLSearchParams({ [RECONCILIATION_TAB_PARAM]: tab });
  if (companyStatus) {
    params.set(RECONCILIATION_COMPANY_STATUS_PARAM, companyStatus);
  }
  return `?${params.toString()}`;
};

/**
 * Shows one salary accrual reconciliation run: its summary, whose counts open the matching tab, and tabs for its
 * report, the problems it found for the first time or resolved, past problems never fixed, problems fixed by hand, the
 * companies it covered and the individuals it could not check. In a company's view every tab is limited to that
 * company.
 */
function SalaryReconciliationProfile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { reconciliationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const company = useManagingCompanyStore((state) => state.company);
  const view = useNavigationStore((state) => state.view);
  const companyId = view === 'company' ? company?.id : undefined;

  const tabKey = searchParams.get(RECONCILIATION_TAB_PARAM);
  const tabIndex = Math.max(
    SalaryReconciliationTabs.findIndex((tab) => tab.key === tabKey),
    0
  );
  const companyStatusParam = searchParams.get(RECONCILIATION_COMPANY_STATUS_PARAM);
  const companyStatus = COMPANY_STATUSES.find((status) => status === companyStatusParam);

  const { data: run, isLoading: isLoadingRun } = useFetchSalaryReconciliation(reconciliationId);
  const isRunning = isActiveReconciliationRun(run);
  const { data: report, isLoading: isLoadingReport } = useFetchSalaryReconciliationReport(
    reconciliationId,
    companyId,
    Boolean(run?.reportAvailable)
  );
  const { mutate: downloadReport, isPending: isDownloading } =
    useDownloadSalaryReconciliationReport();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all(
        [
          SALARY_RECONCILIATION_QUERY_KEY,
          SALARY_RECONCILIATION_REPORT_QUERY_KEY,
          SALARY_DISCREPANCIES_QUERY_KEY,
          SALARY_RECONCILIATION_COMPANIES_QUERY_KEY,
          SALARY_UNCHECKED_INDIVIDUALS_QUERY_KEY,
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleTabChange = (index: number) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set(RECONCILIATION_TAB_PARAM, SalaryReconciliationTabs[index].key);
      return params;
    });
  };

  const handleCompanyStatusChange = (status?: SalaryReconciliationCompanyStatus) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set(RECONCILIATION_TAB_PARAM, 'companies');
      if (status) {
        params.set(RECONCILIATION_COMPANY_STATUS_PARAM, status);
      } else {
        params.delete(RECONCILIATION_COMPANY_STATUS_PARAM);
      }
      return params;
    });
  };

  if (isLoadingRun) {
    return (
      <AuthenticatedPageWrapper title="Salary reconciliation">
        <Center w="full" h="full" flexGrow={1} data-testid="salary-reconciliation-loading">
          <Spinner color="brand.purple" size="xl" />
        </Center>
      </AuthenticatedPageWrapper>
    );
  }

  if (!run || !reconciliationId) {
    return (
      <AuthenticatedPageWrapper title="Salary reconciliation">
        <EmptyState text="This reconciliation could not be found" />
      </AuthenticatedPageWrapper>
    );
  }

  const status = ReconciliationRunStatusValues[run.status];
  const hasReport = run.reportAvailable && report !== null;
  const triggeredBy = run.automated ? 'Nightly run' : run.triggeredByName || 'An admin';

  return (
    <AuthenticatedPageWrapper title="Salary reconciliation">
      <Button
        my="10px"
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => navigate(getReconciliationsPath('salary'))}
        data-testid="back-to-salary-reconciliations"
      >
        Back to reconciliations
      </Button>

      <Flex align="center" justify="space-between" gap="20px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Salary reconciliation {formatDateTime(run.startedAt ?? run.queuedAt)}
        </Text>
        <Flex gap="12px" flexShrink={0}>
          <RefreshButton
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            testId="refresh-salary-reconciliation-button"
          />
          <Button
            leftIcon={<DocumentDownloadOutline boxSize="20px" />}
            size="sm"
            isDisabled={!hasReport}
            isLoading={isDownloading}
            onClick={() => downloadReport({ id: reconciliationId, companyId })}
            data-testid="download-salary-reconciliation-report-button"
          >
            Download report
          </Button>
        </Flex>
      </Flex>

      <SimpleGrid
        mt="20px"
        w="full"
        columns={{ base: 1, md: 3, xl: 5 }}
        spacing="20px"
        p="20px"
        border="1px solid"
        borderColor="brand.lightGray1"
        borderRadius="10px"
        data-testid="salary-reconciliation-summary"
      >
        <Flex gap="5px" flexDir="column" w="full">
          <Text color="brand.black" textStyle="extraSmallRegular">
            Status
          </Text>
          <Flex minH="52px" align="center">
            <Badge
              value={status?.value ?? run.status}
              status={(status?.color ?? 'created') as BadgeStatus}
            />
          </Flex>
        </Flex>
        <ProfileField label="Reconciliation ID" value={run.id} copyToClipboard />
        <ProfileField label="Triggered by" value={triggeredBy} />
        <ProfileField label="Queued" value={formatDateTime(run.queuedAt)} />
        <ProfileField label="Started" value={formatDateTime(run.startedAt)} />
        <ProfileField label="Finished" value={formatDateTime(run.completedAt)} />
        <ProfileField label="Duration" value={formatRunDuration(run)} />
        <ProfileField
          label="Companies checked"
          value={String(run.companiesChecked)}
          link={tabLink('companies', 'CHECKED')}
        />
        <ProfileField
          label="Companies failed"
          value={String(run.companiesFailed)}
          link={tabLink('companies', 'FAILED')}
        />
        <ProfileField label="Individuals checked" value={String(run.individualsChecked)} />
        <ProfileField
          label="Individuals not checked"
          value={String(run.individualsNotChecked)}
          link={tabLink('not-checked')}
        />
        <ProfileField
          label="Problems found"
          value={String(run.problems)}
          link={tabLink('problems')}
        />
        <ProfileField label="New problems" value={String(run.newProblems)} link={tabLink('new')} />
        <ProfileField
          label="Resolved problems"
          value={String(run.resolvedProblems)}
          link={tabLink('resolved')}
        />
      </SimpleGrid>

      {run.error && (
        <Flex
          mt="20px"
          p="16px"
          borderRadius="10px"
          bgColor="red.50"
          color="red.700"
          data-testid="salary-reconciliation-error"
        >
          <Text>{run.error}</Text>
        </Flex>
      )}

      <Tabs mt="30px" w="full" index={tabIndex} onChange={handleTabChange} isLazy>
        <TabList>
          {SalaryReconciliationTabs.map((tab) => (
            <Tab key={tab.key} data-testid={`salary-reconciliation-tab-${tab.key}`}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt="24px">
            <SalaryReconciliationProblemsTab
              rows={report?.rows ?? []}
              isRunning={isRunning}
              hasReport={hasReport}
              isLoadingReport={isLoadingReport}
              showCompanyColumn={view === 'global'}
            />
          </TabPanel>
          {DISCREPANCY_TABS.map((mode) => (
            <TabPanel key={mode} px={0} pt="24px">
              <SalaryDiscrepanciesTab
                reconciliationId={reconciliationId}
                mode={mode}
                companyId={companyId}
                isRunning={isRunning}
              />
            </TabPanel>
          ))}
          <TabPanel px={0} pt="24px">
            <SalaryReconciliationCompaniesTab
              reconciliationId={reconciliationId}
              companyId={companyId}
              status={companyStatus}
              onStatusChange={handleCompanyStatusChange}
              isRunning={isRunning}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <SalaryNotCheckedTab
              reconciliationId={reconciliationId}
              companyId={companyId}
              isRunning={isRunning}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </AuthenticatedPageWrapper>
  );
}

export default SalaryReconciliationProfile;
